import type { TuningHistogram, TuningTelemetryWindow } from '../contracts/measurements';

/** Keeps fresh gauges while carrying only compatible, non-overlapping histograms forward. */
export class TelemetryPendingWindow {
  private pending: TuningTelemetryWindow | undefined;
  private coalesced = 0;
  private histogramSamplesDropped = 0;

  constructor(private readonly fits: (window: TuningTelemetryWindow) => boolean) {}

  push(window: TuningTelemetryWindow): void {
    const previous = this.pending;
    if (!previous) { this.pending = window; return; }
    this.coalesced++;
    if (!this.sameSegment(previous, window)) {
      this.dropHistograms(previous);
      this.pending = window;
      return;
    }
    const distributions = { ...window.distributions };
    let dropped = 0;
    for (const [key, histogram] of Object.entries(previous.distributions)) {
      const latest = distributions[key];
      if (!latest) distributions[key] = histogram;
      else if (this.mergeable(histogram, latest)) distributions[key] = this.merge(histogram, latest);
      else dropped += histogram.sampleCount;
    }
    const merged = { ...window, distributions };
    if (this.fits(merged)) { this.pending = merged; this.histogramSamplesDropped += dropped; }
    else { this.dropHistograms(previous); this.pending = window; }
  }

  peek(): TuningTelemetryWindow | undefined { return this.pending; }
  take(): TuningTelemetryWindow | undefined {
    const pending = this.pending;
    this.pending = undefined;
    return pending;
  }

  discard(): void {
    if (this.pending) { this.coalesced++; this.dropHistograms(this.pending); }
    this.pending = undefined;
  }

  snapshot(): { coalesced: number; histogramSamplesDropped: number } {
    return { coalesced: this.coalesced, histogramSamplesDropped: this.histogramSamplesDropped };
  }

  private sameSegment(a: TuningTelemetryWindow, b: TuningTelemetryWindow): boolean {
    return a.target.sessionId === b.target.sessionId && a.target.peerId === b.target.peerId
      && a.target.trialId === b.target.trialId && a.target.connectionEpoch === b.target.connectionEpoch
      && a.target.configRevision === b.target.configRevision && a.endpoint === b.endpoint
      && a.endpointConnectionEpoch === b.endpointConnectionEpoch && a.pathEpoch === b.pathEpoch
      && a.sourceId === b.sourceId && a.captureGeneration === b.captureGeneration
      && a.clock.state === b.clock.state && a.clock.offsetMs === b.clock.offsetMs
      && a.visibility === b.visibility && a.monotonicEndMs <= b.monotonicStartMs;
  }

  private mergeable(a: TuningHistogram, b: TuningHistogram): boolean {
    return a.schema === b.schema && a.unit === b.unit && a.source === b.source
      && a.availability === 'measured' && b.availability === 'measured'
      && a.monotonicEndMs <= b.monotonicStartMs && Number.isSafeInteger(a.sampleCount + b.sampleCount);
  }

  private merge(a: TuningHistogram, b: TuningHistogram): TuningHistogram {
    const counts = new Map<number, number>();
    for (const [index, count] of [...a.buckets, ...b.buckets]) counts.set(index, (counts.get(index) ?? 0) + count);
    const maxima = [a.maxMs, b.maxMs].filter((value): value is number => value !== null);
    return { ...b, monotonicStartMs: a.monotonicStartMs,
      buckets: [...counts.entries()].sort(([left], [right]) => left - right),
      sampleCount: a.sampleCount + b.sampleCount, maxMs: maxima.length ? Math.max(...maxima) : null };
  }

  private dropHistograms(window: TuningTelemetryWindow): void {
    for (const histogram of Object.values(window.distributions)) this.histogramSamplesDropped += histogram.sampleCount;
  }
}
